import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';

import {
	addPotentialContact,
	getContactsToAdd,
	ignoreContactToAdd
} from '../../../../../../../actions/ContactDBAccessLog';
import {updateDisplayData} from '../../../../../../../actions/Metrics/dashboardContextAction';
import {raiseJSValidationError} from '../../../../../../../actions/erroractions';

import {labels, currentResource} from '../../../../../../../util/utils';
import {concurrencyErrorCode, inventedConcurrencyErrorCode} from '../../../../../../../util/uiconstants';
import isContactListEmpty from '../isContactListEmpty';

import {IconoirPlus, IconoirProhibition} from '@ey-xd/motif-icon';

import Button from '../../../../../../CoreWrappers/Button';
import BorderBox from '../../../../../../Common/BorderBox';
import UserList from '../UserList';
import normalizeAddContact from './normalizeAddContact';
import ConfirmationModal from './ConfirmationModal';

const ContactsToAdd = ({setIsLoading, renderEmpty = null}) => {
	const contactsToAdd = useSelector(({contactsAccessLog}) => contactsAccessLog.contactsToAdd);
	const project = useSelector(({project}) => project);
	const engagements = useSelector(({dashboardContext}) => dashboardContext?.engagements);
	const entities = useSelector(({dashboardContext}) => dashboardContext?.entities);
	const orgChart = useSelector(({dashboardContext}) => dashboardContext?.orgChart);
	const engagementMetadata = useSelector((store) => store?.engagementMetaData);
	const dispatch = useDispatch();

	const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
	const [missingDomains, setMissingDomains] = useState({});
	const [user, setUser] = useState();
	const [entityId, setEntityId] = useState();

	useEffect(() => {
		if (contactsToAdd === null) {
			setIsLoading(true);
			dispatch(getContactsToAdd(project?.projectId, orgChart))
				.then(() => setIsLoading(false))
				.catch(() => setIsLoading(false));
		}
	}, [contactsToAdd]);

	const displayCustomError = (error, users) => {
		setIsLoading(false);
		const errorCode = error?.response?.data?.errors?.Error[0]?.split('-')[0].trim() || '';
		if (errorCode == concurrencyErrorCode) {
			dispatch(
				raiseJSValidationError(
					inventedConcurrencyErrorCode +
						' - ' +
						currentResource.Errors[errorCode].replace('{0}', users?.name + ' ' + users?.lastName)
				)
			);
		}
	};

	const handleIgnore = (user, entityId) => {
		setIsLoading(true);
		dispatch(ignoreContactToAdd(project?.projectId, user.userId, user, entityId))
			.then(() => setIsLoading(false))
			.catch((error) => displayCustomError(error, user));
	};

	const dispatchAddPotentialContact = (user, entityId) => {
		setIsLoading(true);
		dispatch(addPotentialContact(project?.projectId, user.userId, user, entityId))
			.then(() => {
				const newEntities = normalizeAddContact(entities, user, entityId, engagementMetadata);
				dispatch(updateDisplayData(engagements, newEntities, orgChart));
				setIsLoading(false);
			})
			.catch((error) => displayCustomError(error, user));
	};

	const handleAdd = (user, entityId) => {
		if (!user?.isInternal) {
			const entityDomains = project.orgStructure.find((x) => x.entityId === entityId)?.domains ?? [];

			const userDomain = `@${user?.emailAddress.split('@')[1]}`;

			const missingDomains = entityDomains?.filter((domain) => {
				let domainPattern = domain.replace('@', '\\@');
				domainPattern = domainPattern.replaceAll('*', '\\w+');
				domainPattern = domainPattern.replaceAll('.', '\\.*');

				let regexPattern = new RegExp(domainPattern);

				return !regexPattern.test(userDomain);
			});

			if (missingDomains.length === entityDomains.length) {
				const missingDomainsPerEntity = [
					{
						entityName: entities?.find((x) => x.entityId === entityId)?.label,
						domains: userDomain
					}
				];

				setMissingDomains(missingDomainsPerEntity);
				setUser(user);
				setEntityId(entityId);
				setOpenConfirmationModal(true);
			} else {
				dispatchAddPotentialContact(user, entityId);
			}
		} else {
			dispatchAddPotentialContact(user, entityId);
		}
	};

	if (contactsToAdd === null) return null;
	if (isContactListEmpty(contactsToAdd) && renderEmpty) return renderEmpty(labels.contactsToAddEmpty);

	const renderControls = (user, entityId) => {
		const addProps = {
			svg: IconoirPlus,
			title: labels.add,
			onClick: () => handleAdd(user, entityId),
			variant: 'ghost',
			iconSize: 20
		};
		const ignoreProps = {
			svg: IconoirProhibition,
			title: labels.ignore,
			onClick: () => handleIgnore(user, entityId),
			variant: 'ghost',
			iconSize: 20
		};
		return (
			<>
				<Button {...addProps} />
				<Button {...ignoreProps} />
			</>
		);
	};

	const renderConfirmationModal = () => {
		const props = {
			missingDomains,
			contactsToAdd: [user],
			projectId: project?.projectId,
			entityId,
			entities,
			engagementMetadata,
			engagements,
			orgChart,
			isAddAll: false,
			message: labels.addConfirmationModal
		};

		return <ConfirmationModal {...props} />;
	};

	const props = {
		entityList: contactsToAdd,
		renderControls
	};

	return (
		<BorderBox>
			<UserList {...props} />
			{openConfirmationModal && renderConfirmationModal()}
		</BorderBox>
	);
};

ContactsToAdd.propTypes = {
	setIsLoading: PropTypes.func.isRequired,
	renderEmpty: PropTypes.func
};

ContactsToAdd.defaultProps = {};

export default ContactsToAdd;
